/**
 * Video Effects Processor - Simplified approach
 * Uses simple Gaussian blur on mask for smooth edges
 */

import { ImageSegmenter, FilesetResolver } from '@mediapipe/tasks-vision';

// Simple vertex shader
const VERTEX_SHADER = `#version 300 es
  in vec2 a_position;
  in vec2 a_texCoord;
  out vec2 v_texCoord;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_texCoord = a_texCoord;
  }
`;

// Mask blur - horizontal (9-tap Gaussian - good balance of quality/speed)
const MASK_BLUR_H_SHADER = `#version 300 es
  precision highp float;
  uniform sampler2D u_mask;
  uniform vec2 u_texelSize;
  in vec2 v_texCoord;
  out vec4 outColor;

  void main() {
    float result = 0.0;
    // 9-tap Gaussian blur (sigma ~2.0)
    result += texture(u_mask, v_texCoord + vec2(-4.0 * u_texelSize.x, 0.0)).r * 0.006;
    result += texture(u_mask, v_texCoord + vec2(-3.0 * u_texelSize.x, 0.0)).r * 0.030;
    result += texture(u_mask, v_texCoord + vec2(-2.0 * u_texelSize.x, 0.0)).r * 0.105;
    result += texture(u_mask, v_texCoord + vec2(-1.0 * u_texelSize.x, 0.0)).r * 0.222;
    result += texture(u_mask, v_texCoord).r * 0.274;
    result += texture(u_mask, v_texCoord + vec2(1.0 * u_texelSize.x, 0.0)).r * 0.222;
    result += texture(u_mask, v_texCoord + vec2(2.0 * u_texelSize.x, 0.0)).r * 0.105;
    result += texture(u_mask, v_texCoord + vec2(3.0 * u_texelSize.x, 0.0)).r * 0.030;
    result += texture(u_mask, v_texCoord + vec2(4.0 * u_texelSize.x, 0.0)).r * 0.006;
    outColor = vec4(result, result, result, 1.0);
  }
`;

// Mask blur - vertical (9-tap Gaussian - good balance of quality/speed)
const MASK_BLUR_V_SHADER = `#version 300 es
  precision highp float;
  uniform sampler2D u_mask;
  uniform vec2 u_texelSize;
  in vec2 v_texCoord;
  out vec4 outColor;

  void main() {
    float result = 0.0;
    // 9-tap Gaussian blur (sigma ~2.0)
    result += texture(u_mask, v_texCoord + vec2(0.0, -4.0 * u_texelSize.y)).r * 0.006;
    result += texture(u_mask, v_texCoord + vec2(0.0, -3.0 * u_texelSize.y)).r * 0.030;
    result += texture(u_mask, v_texCoord + vec2(0.0, -2.0 * u_texelSize.y)).r * 0.105;
    result += texture(u_mask, v_texCoord + vec2(0.0, -1.0 * u_texelSize.y)).r * 0.222;
    result += texture(u_mask, v_texCoord).r * 0.274;
    result += texture(u_mask, v_texCoord + vec2(0.0, 1.0 * u_texelSize.y)).r * 0.222;
    result += texture(u_mask, v_texCoord + vec2(0.0, 2.0 * u_texelSize.y)).r * 0.105;
    result += texture(u_mask, v_texCoord + vec2(0.0, 3.0 * u_texelSize.y)).r * 0.030;
    result += texture(u_mask, v_texCoord + vec2(0.0, 4.0 * u_texelSize.y)).r * 0.006;
    outColor = vec4(result, result, result, 1.0);
  }
`;

// Erosion shader - shrinks the mask to remove small false positives (noise, distant objects)
const MASK_ERODE_SHADER = `#version 300 es
  precision highp float;
  uniform sampler2D u_mask;
  uniform vec2 u_texelSize;
  in vec2 v_texCoord;
  out vec4 outColor;

  void main() {
    // 3x3 erosion - take minimum of neighborhood
    float minVal = 1.0;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 offset = vec2(float(x), float(y)) * u_texelSize;
        float val = texture(u_mask, v_texCoord + offset).r;
        minVal = min(minVal, val);
      }
    }
    outColor = vec4(minVal, minVal, minVal, 1.0);
  }
`;

// Dilation shader - expands the mask to recover "eaten" edges
const MASK_DILATE_SHADER = `#version 300 es
  precision highp float;
  uniform sampler2D u_mask;
  uniform vec2 u_texelSize;
  in vec2 v_texCoord;
  out vec4 outColor;

  void main() {
    // 3x3 dilation - take maximum of neighborhood
    float maxVal = 0.0;
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 offset = vec2(float(x), float(y)) * u_texelSize;
        float val = texture(u_mask, v_texCoord + offset).r;
        maxVal = max(maxVal, val);
      }
    }
    outColor = vec4(maxVal, maxVal, maxVal, 1.0);
  }
`;

// Background blur - horizontal (13-tap for smooth blur)
const BG_BLUR_H_SHADER = `#version 300 es
  precision highp float;
  uniform sampler2D u_image;
  uniform vec2 u_texelSize;
  uniform float u_blurAmount;
  in vec2 v_texCoord;
  out vec4 outColor;

  void main() {
    vec4 result = vec4(0.0);
    float total = 0.0;

    // 13-tap Gaussian kernel for smooth blur
    float offsets[7] = float[](0.0, 1.0, 2.0, 3.0, 4.0, 5.0, 6.0);
    float weights[7] = float[](0.20, 0.17, 0.12, 0.08, 0.04, 0.02, 0.01);

    for (int i = 0; i < 7; i++) {
      float offset = offsets[i] * u_blurAmount * u_texelSize.x;
      float weight = weights[i];

      result += texture(u_image, v_texCoord + vec2(offset, 0.0)) * weight;
      total += weight;

      if (i > 0) {
        result += texture(u_image, v_texCoord - vec2(offset, 0.0)) * weight;
        total += weight;
      }
    }
    outColor = result / total;
  }
`;

// Background blur - vertical (13-tap for smooth blur)
const BG_BLUR_V_SHADER = `#version 300 es
  precision highp float;
  uniform sampler2D u_image;
  uniform vec2 u_texelSize;
  uniform float u_blurAmount;
  in vec2 v_texCoord;
  out vec4 outColor;

  void main() {
    vec4 result = vec4(0.0);
    float total = 0.0;

    // 13-tap Gaussian kernel for smooth blur
    float offsets[7] = float[](0.0, 1.0, 2.0, 3.0, 4.0, 5.0, 6.0);
    float weights[7] = float[](0.20, 0.17, 0.12, 0.08, 0.04, 0.02, 0.01);

    for (int i = 0; i < 7; i++) {
      float offset = offsets[i] * u_blurAmount * u_texelSize.y;
      float weight = weights[i];

      result += texture(u_image, v_texCoord + vec2(0.0, offset)) * weight;
      total += weight;

      if (i > 0) {
        result += texture(u_image, v_texCoord - vec2(0.0, offset)) * weight;
        total += weight;
      }
    }
    outColor = result / total;
  }
`;

// Composite with smooth antialiased edges
const COMPOSITE_SHADER = `#version 300 es
  precision highp float;
  uniform sampler2D u_original;
  uniform sampler2D u_blurred;
  uniform sampler2D u_mask;
  uniform sampler2D u_background;
  uniform int u_useBackground;
  in vec2 v_texCoord;
  out vec4 outColor;

  // Quintic smoothstep for extra smooth antialiasing (smoother than cubic)
  float smootherstep(float t) {
    t = clamp(t, 0.0, 1.0);
    return t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
  }

  void main() {
    vec4 original = texture(u_original, v_texCoord);
    vec4 blurred = texture(u_blurred, v_texCoord);
    float mask = texture(u_mask, v_texCoord).r;

    // Wide transition zone for maximum antialiasing:
    // 0.00-0.35: definitely background (alpha = 0)
    // 0.35-0.65: wide transition zone (30% width) with quintic smoothstep
    // 0.65-1.00: definitely person (alpha = 1)

    float alpha;
    if (mask < 0.35) {
      alpha = 0.0;
    } else if (mask > 0.65) {
      alpha = 1.0;
    } else {
      // Extra smooth quintic interpolation for antialiasing
      float t = (mask - 0.35) / 0.30;
      alpha = smootherstep(t);
    }

    if (u_useBackground == 1) {
      // Mirror background horizontally so it appears correct when video is displayed with scaleX(-1)
      vec4 bg = texture(u_background, vec2(1.0 - v_texCoord.x, v_texCoord.y));
      outColor = mix(bg, original, alpha);
    } else {
      outColor = mix(blurred, original, alpha);
    }
  }
`;

// Quality multipliers - HIGHER resolution for better edges
const QUALITY_LEVELS = {
  high: {
    name: 'Ultra',
    model: 'selfie_segmenter',
    modelUrl: 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
    multiplier: 1.0,
    maxWidth: 1280,  // Full HD segmentation
    maxHeight: 720,
    targetFps: 30,
  },
  medium: {
    name: 'High',
    model: 'selfie_segmenter',
    modelUrl: 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
    multiplier: 0.85,
    maxWidth: 1024,  // Increased from 960
    maxHeight: 576,
    targetFps: 30,
  },
  low: {
    name: 'Medium',
    model: 'selfie_segmenter',
    modelUrl: 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
    multiplier: 0.65,
    maxWidth: 800,   // Increased from 640
    maxHeight: 448,
    targetFps: 30,
  },
};

// Calculate optimal segment dimensions based on camera resolution (maintains aspect ratio)
function calculateSegmentDimensions(cameraWidth, cameraHeight, qualityConfig) {
  // Apply multiplier to both dimensions
  let width = Math.round(cameraWidth * qualityConfig.multiplier);
  let height = Math.round(cameraHeight * qualityConfig.multiplier);

  // Cap at max dimensions
  if (width > qualityConfig.maxWidth) {
    const scale = qualityConfig.maxWidth / width;
    width = qualityConfig.maxWidth;
    height = Math.round(height * scale);
  }
  if (height > qualityConfig.maxHeight) {
    const scale = qualityConfig.maxHeight / height;
    height = qualityConfig.maxHeight;
    width = Math.round(width * scale);
  }

  // Ensure minimum of 256 for decent quality
  width = Math.max(width, 256);
  height = Math.max(height, 144);

  // Round to multiple of 32 for GPU efficiency
  width = Math.round(width / 32) * 32;
  height = Math.round(height / 32) * 32;

  return { width, height };
}

class VideoEffectsProcessor {
  constructor() {
    this.segmenter = null;
    this.isInitialized = false;
    this.isProcessing = false;
    this.gl = null;
    this.programs = {};
    this.textures = {};
    this.framebuffers = {};
    this.outputCanvas = null;
    this.segmentCanvas = null;
    this.segmentCtx = null;
    this.effectType = 'none';
    this.blurLevel = 'medium';
    this.backgroundImage = null;
    this.sourceVideo = null;
    this.outputStream = null;
    this.animationFrameId = null;
    this.lastProcessTime = 0;
    this.frameCount = 0;
    this.sourceWidth = 1280;
    this.sourceHeight = 720;
    this.targetFps = 30;

    // Adaptive quality system
    this.qualityLevel = 'high';
    this.currentQualityConfig = QUALITY_LEVELS.high;
    this.autoAdaptQuality = true;

    // Performance monitoring
    this.debugEnabled = true;
    this.performanceStats = {
      fps: 0,
      avgProcessingTime: 0,
      frameTimestamps: [],
      processingTimes: [],
      droppedFrames: 0,
      qualityChanges: [],
    };
    this.lastQualityCheck = 0;
    this.qualityCheckInterval = 2000; // Check every 2 seconds
    this.skipFrameCounter = 0;
    this.lastMaskData = null;  // For frame skipping

    // Temporal smoothing
    this.previousMaskFloat = null;
    this.temporalAlpha = 0.35;  // Balanced: enough smoothing, less ghost

    // Depth estimation integration
    this.depthService = null;
    this.depthMaskData = null;  // Cached RAW depth at segment resolution (0=far, 1=close)
    this.depthMaskWidth = 0;
    this.depthMaskHeight = 0;
    this.lastDepthCheckFrame = 0;
    this.depthCheckInterval = 30;  // Check for scene change every 30 frames
    this.depthInfluence = 0.4;  // How much depth affects uncertain pixels (0-1)
    this.useDepthOnly = false;  // Use depth as primary mask instead of MediaPipe
    this.depthThresholdLow = 0.35;  // Below this = definitely background
    this.depthThresholdHigh = 0.55; // Above this = definitely foreground
    this.depthRescueThreshold = 0.65; // If depth > this, rescue pixels even if MediaPipe says background
    this.depthSuppressThreshold = 0.35; // If depth < this, suppress pixels even if MediaPipe says person
  }

  static checkSupport() {
    try {
      const c = document.createElement('canvas');
      return !!c.getContext('webgl2');
    } catch (e) {
      return false;
    }
  }

  // ============== DEBUG & MONITORING METHODS ==============

  // Update performance stats after each frame
  updatePerformanceStats(processingTime) {
    const now = performance.now();
    const stats = this.performanceStats;

    // Track processing times (keep last 60 samples)
    stats.processingTimes.push(processingTime);
    if (stats.processingTimes.length > 60) {
      stats.processingTimes.shift();
    }

    // Track frame timestamps for FPS calculation
    stats.frameTimestamps.push(now);
    if (stats.frameTimestamps.length > 60) {
      stats.frameTimestamps.shift();
    }

    // Calculate average processing time
    stats.avgProcessingTime = stats.processingTimes.reduce((a, b) => a + b, 0) / stats.processingTimes.length;

    // Calculate FPS from timestamps
    if (stats.frameTimestamps.length >= 2) {
      const timeSpan = stats.frameTimestamps[stats.frameTimestamps.length - 1] - stats.frameTimestamps[0];
      stats.fps = Math.round((stats.frameTimestamps.length - 1) / (timeSpan / 1000));
    }

    // Check if we should adapt quality
    if (this.autoAdaptQuality && now - this.lastQualityCheck > this.qualityCheckInterval) {
      this.checkAndAdaptQuality();
      this.lastQualityCheck = now;
    }
  }

  // Automatic quality adaptation based on performance
  checkAndAdaptQuality() {
    const stats = this.performanceStats;
    const currentLevel = this.qualityLevel;
    let newLevel = currentLevel;

    // Downgrade conditions
    if (stats.fps < 20 || stats.avgProcessingTime > 50) {
      if (currentLevel === 'high') newLevel = 'medium';
      else if (currentLevel === 'medium') newLevel = 'low';
    }
    // Upgrade conditions (only if stable for a while)
    else if (stats.fps >= 28 && stats.avgProcessingTime < 25) {
      if (currentLevel === 'low') newLevel = 'medium';
      else if (currentLevel === 'medium') newLevel = 'high';
    }

    if (newLevel !== currentLevel) {
      console.log(`[VideoEffects] Quality change: ${currentLevel} → ${newLevel} (FPS: ${stats.fps}, Proc: ${stats.avgProcessingTime.toFixed(1)}ms)`);
      stats.qualityChanges.push({
        time: new Date().toISOString(),
        from: currentLevel,
        to: newLevel,
        fps: stats.fps,
        processingTime: stats.avgProcessingTime,
      });
      this.setQualityLevel(newLevel);
    }
  }

  // Set quality level manually
  async setQualityLevel(level) {
    if (!QUALITY_LEVELS[level]) {
      console.error('[VideoEffects] Invalid quality level:', level);
      return;
    }

    this.qualityLevel = level;
    this.currentQualityConfig = QUALITY_LEVELS[level];
    this.targetFps = this.currentQualityConfig.targetFps;

    // Recalculate segment canvas dimensions based on camera resolution
    if (this.segmentCanvas && this.sourceWidth && this.sourceHeight) {
      const segmentDims = calculateSegmentDimensions(this.sourceWidth, this.sourceHeight, this.currentQualityConfig);
      this.segmentCanvas.width = segmentDims.width;
      this.segmentCanvas.height = segmentDims.height;
      this.currentSegmentWidth = segmentDims.width;
      this.currentSegmentHeight = segmentDims.height;
      console.log(`[VideoEffects] Quality set to: ${level} (${this.currentQualityConfig.name}) → Segment: ${segmentDims.width}×${segmentDims.height}`);
    } else {
      console.log(`[VideoEffects] Quality set to: ${level} (${this.currentQualityConfig.name})`);
    }
  }

  // Get current debug info
  getDebugInfo() {
    return {
      qualityLevel: this.qualityLevel,
      qualityName: this.currentQualityConfig.name,
      cameraResolution: `${this.sourceWidth}×${this.sourceHeight}`,
      segmentSize: this.segmentCanvas ?
        `${this.segmentCanvas.width}×${this.segmentCanvas.height}` : 'N/A',
      fps: this.performanceStats.fps,
      avgProcessingTime: Math.round(this.performanceStats.avgProcessingTime * 10) / 10,
      droppedFrames: this.performanceStats.droppedFrames,
      frameCount: this.frameCount,
      isProcessing: this.isProcessing,
      effectType: this.effectType,
      blurLevel: this.blurLevel,
      autoAdapt: this.autoAdaptQuality,
      recentQualityChanges: this.performanceStats.qualityChanges.slice(-5),
    };
  }

  // Log debug info to console
  logDebugInfo() {
    const info = this.getDebugInfo();
    console.log('[VideoEffects] Debug Info:', info);
    return info;
  }

  // Enable/disable auto quality adaptation
  setAutoAdaptQuality(enabled) {
    this.autoAdaptQuality = enabled;
    console.log(`[VideoEffects] Auto-adapt quality: ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Enable/disable debug logging
  setDebugEnabled(enabled) {
    this.debugEnabled = enabled;
  }

  // ============== DEPTH ESTIMATION METHODS ==============

  /**
   * Initialize depth estimation service (lazy loaded)
   * Called automatically when effects start, runs in background
   */
  async initDepthEstimation() {
    if (this.depthService) return;

    try {
      const { getDepthEstimationService } = await import('./depthEstimation.js');
      this.depthService = getDepthEstimationService();

      console.log('[VideoEffects] Loading depth estimation model...');
      await this.depthService.initialize((progress) => {
        if (progress.status === 'downloading') {
          console.log(`[VideoEffects] Depth model: ${progress.percent}%`);
        }
      });
      console.log('[VideoEffects] Depth estimation ready');

      // Run initial depth estimation after a short delay
      setTimeout(() => this.updateDepthMask(), 1000);
    } catch (error) {
      console.error('[VideoEffects] Failed to load depth estimation:', error);
      this.depthService = null;
    }
  }

  /**
   * Update depth mask from current video frame
   * Called occasionally (startup + scene changes)
   */
  async updateDepthMask() {
    if (!this.depthService || !this.sourceVideo || !this.isProcessing) return;
    if (!this.depthService.isInitialized) return;  // Wait for pipeline to be ready

    try {
      const result = await this.depthService.estimateDepth(this.sourceVideo);
      if (!result) return;

      // Get RAW depth values (not binary mask) resized to match segment canvas
      const segW = this.segmentCanvas?.width || 640;
      const segH = this.segmentCanvas?.height || 360;

      // Use raw depth values normalized 0-1 (0=far, 1=close)
      // This is much better than binary mask for edge refinement
      const rawDepth = this.depthService.getRawDepthNormalized(segW, segH);
      if (rawDepth) {
        this.depthMaskData = rawDepth;
        this.depthMaskWidth = segW;
        this.depthMaskHeight = segH;
        console.log(`[VideoEffects] Raw depth updated (${segW}×${segH}) - using continuous values`);
      }
    } catch (error) {
      console.error('[VideoEffects] Depth update failed:', error);
    }
  }

  /**
   * Check for scene changes and trigger depth re-estimation
   */
  checkSceneChange() {
    // Only check if depth service is fully initialized
    if (!this.depthService || !this.sourceVideo) return;
    if (!this.depthService.isInitialized) return;  // Wait for pipeline to be ready

    if (this.depthService.detectSceneChange(this.sourceVideo)) {
      console.log('[VideoEffects] Scene change detected, updating depth...');
      this.updateDepthMask();
    }
  }

  // ============== END DEPTH METHODS ==============

  // ============== END DEBUG METHODS ==============

  async initialize() {
    if (this.isInitialized) return true;

    console.log('[VideoEffects] Initializing...');

    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    );

    // Selfie segmenter with confidence masks for smooth edges
    this.segmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    });

    this.isInitialized = true;
    console.log('[VideoEffects] Ready');
    return true;
  }

  initWebGL() {
    if (this.gl) return;

    this.outputCanvas = document.createElement('canvas');
    this.outputCanvas.width = this.sourceWidth;
    this.outputCanvas.height = this.sourceHeight;

    this.gl = this.outputCanvas.getContext('webgl2', {
      premultipliedAlpha: false,
      preserveDrawingBuffer: true,
    });

    const gl = this.gl;

    // Create programs
    this.programs.maskBlurH = this.createProgram(MASK_BLUR_H_SHADER);
    this.programs.maskBlurV = this.createProgram(MASK_BLUR_V_SHADER);
    this.programs.maskErode = this.createProgram(MASK_ERODE_SHADER);
    this.programs.maskDilate = this.createProgram(MASK_DILATE_SHADER);
    this.programs.bgBlurH = this.createProgram(BG_BLUR_H_SHADER);
    this.programs.bgBlurV = this.createProgram(BG_BLUR_V_SHADER);
    this.programs.composite = this.createProgram(COMPOSITE_SHADER);

    // Create textures
    this.textures.video = this.createTexture();
    this.textures.mask = this.createTexture();
    this.textures.maskBlurred = this.createTexture();
    this.textures.maskTemp = this.createTexture();
    this.textures.bgBlurred = this.createTexture();
    this.textures.bgTemp = this.createTexture();
    this.textures.background = this.createTexture();

    // Create framebuffers
    this.framebuffers.maskTemp = this.createFramebuffer(this.textures.maskTemp);
    this.framebuffers.maskBlurred = this.createFramebuffer(this.textures.maskBlurred);
    this.framebuffers.bgTemp = this.createFramebuffer(this.textures.bgTemp);
    this.framebuffers.bgBlurred = this.createFramebuffer(this.textures.bgBlurred);

    // Setup geometry for all programs
    this.setupGeometry();

    console.log('[VideoEffects] WebGL ready');
  }

  createProgram(fragmentSrc) {
    const gl = this.gl;
    const vs = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(vs, VERTEX_SHADER);
    gl.compileShader(vs);

    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(fs, fragmentSrc);
    gl.compileShader(fs);

    if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
      console.error('Shader error:', gl.getShaderInfoLog(fs));
    }

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    return prog;
  }

  createTexture() {
    const gl = this.gl;
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return tex;
  }

  createFramebuffer(texture) {
    const gl = this.gl;
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return fb;
  }

  setupGeometry() {
    const gl = this.gl;
    const positions = new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]);
    const texCoords = new Float32Array([0,1, 1,1, 0,0, 0,0, 1,1, 1,0]);

    for (const prog of Object.values(this.programs)) {
      gl.useProgram(prog);

      const posBuf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
      gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
      const posLoc = gl.getAttribLocation(prog, 'a_position');
      if (posLoc >= 0) {
        gl.enableVertexAttribArray(posLoc);
        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);
      }

      const texBuf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, texBuf);
      gl.bufferData(gl.ARRAY_BUFFER, texCoords, gl.STATIC_DRAW);
      const texLoc = gl.getAttribLocation(prog, 'a_texCoord');
      if (texLoc >= 0) {
        gl.enableVertexAttribArray(texLoc);
        gl.vertexAttribPointer(texLoc, 2, gl.FLOAT, false, 0, 0);
      }
    }
  }

  startProcessing(videoElement) {
    if (!this.isInitialized) return null;
    if (this.isProcessing && this.outputStream) return this.outputStream;

    this.sourceVideo = videoElement;
    this.sourceWidth = videoElement.videoWidth || 1280;
    this.sourceHeight = videoElement.videoHeight || 720;

    // Segmentation canvas - size based on camera resolution and quality level
    if (!this.segmentCanvas) {
      this.segmentCanvas = document.createElement('canvas');
      this.segmentCtx = this.segmentCanvas.getContext('2d', { willReadFrequently: true });
    }

    // Mask upscale canvas - for upscaling segment-res mask to full resolution
    if (!this.maskUpscaleCanvas) {
      this.maskUpscaleCanvas = document.createElement('canvas');
      this.maskUpscaleCtx = this.maskUpscaleCanvas.getContext('2d', { willReadFrequently: false });
    }

    // Calculate optimal segment dimensions based on actual camera resolution (maintains aspect ratio)
    const segmentDims = calculateSegmentDimensions(this.sourceWidth, this.sourceHeight, this.currentQualityConfig);
    this.segmentCanvas.width = segmentDims.width;
    this.segmentCanvas.height = segmentDims.height;
    this.currentSegmentWidth = segmentDims.width;
    this.currentSegmentHeight = segmentDims.height;

    // Set mask upscale canvas to full resolution
    this.maskUpscaleCanvas.width = this.sourceWidth;
    this.maskUpscaleCanvas.height = this.sourceHeight;

    console.log(`[VideoEffects] Camera: ${this.sourceWidth}×${this.sourceHeight} → Segment: ${segmentDims.width}×${segmentDims.height} (${this.currentQualityConfig.name})`);

    this.initWebGL();
    this.outputCanvas.width = this.sourceWidth;
    this.outputCanvas.height = this.sourceHeight;
    this.initTextures();

    this.isProcessing = true;
    if (!this.outputStream) {
      this.outputStream = this.outputCanvas.captureStream(this.targetFps);
    }

    // Start depth estimation in background (non-blocking)
    this.initDepthEstimation();

    this.processFrame();
    return this.outputStream;
  }

  initTextures() {
    const gl = this.gl;
    const w = this.sourceWidth;
    const h = this.sourceHeight;

    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    gl.bindTexture(gl.TEXTURE_2D, this.textures.maskTemp);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    gl.bindTexture(gl.TEXTURE_2D, this.textures.maskBlurred);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    gl.bindTexture(gl.TEXTURE_2D, this.textures.bgTemp);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    gl.bindTexture(gl.TEXTURE_2D, this.textures.bgBlurred);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  }

  processFrame = () => {
    if (!this.isProcessing || !this.sourceVideo) return;

    const now = performance.now();
    if (now - this.lastProcessTime < (1000 / this.targetFps) * 0.9) {
      this.animationFrameId = requestAnimationFrame(this.processFrame);
      return;
    }

    if (this.sourceVideo.readyState < 2) {
      this.animationFrameId = requestAnimationFrame(this.processFrame);
      return;
    }

    const frameStart = performance.now();
    this.frameCount++;

    // Periodic scene change check for depth re-estimation
    if (this.depthService && this.frameCount - this.lastDepthCheckFrame >= this.depthCheckInterval) {
      this.lastDepthCheckFrame = this.frameCount;
      this.checkSceneChange();
    }

    try {
      if (this.effectType === 'none') {
        this.renderPassthrough();
      } else {
        // Frame skipping for low quality mode
        const skipFrames = this.currentQualityConfig.skipFrames || 0;
        if (skipFrames > 0 && this.skipFrameCounter < skipFrames && this.lastMaskData) {
          // Reuse last mask
          this.skipFrameCounter++;
          this.renderWithCachedMask(now);
        } else {
          this.skipFrameCounter = 0;
          this.renderWithEffect(now);
        }
      }
    } catch (e) {
      console.error('[VideoEffects] Error:', e);
      this.performanceStats.droppedFrames++;
      this.renderPassthrough();
    }

    // Update performance stats
    const processingTime = performance.now() - frameStart;
    this.updatePerformanceStats(processingTime);

    this.lastProcessTime = now;
    this.animationFrameId = requestAnimationFrame(this.processFrame);
  };

  renderPassthrough() {
    const gl = this.gl;
    const w = this.sourceWidth;
    const h = this.sourceHeight;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);

    gl.useProgram(this.programs.composite);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.sourceVideo);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_original'), 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_blurred'), 1);

    // White mask = all person
    const white = new Uint8Array(this.sourceWidth * this.sourceHeight * 4).fill(255);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.sourceWidth, this.sourceHeight, 0, gl.RGBA, gl.UNSIGNED_BYTE, white);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_mask'), 2);

    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_useBackground'), 0);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  renderWithEffect(timestamp) {
    const gl = this.gl;
    const w = this.sourceWidth;
    const h = this.sourceHeight;

    // Get segment dimensions from current quality config
    const segW = this.segmentCanvas.width;
    const segH = this.segmentCanvas.height;

    // 1. Run segmentation
    this.segmentCtx.drawImage(this.sourceVideo, 0, 0, segW, segH);
    const result = this.segmenter.segmentForVideo(this.segmentCanvas, timestamp);

    const masks = result.confidenceMasks;
    if (!masks || masks.length === 0) {
      this.renderPassthrough();
      result.close();
      return;
    }

    const mask = masks[0];
    const maskData = mask.getAsFloat32Array();

    // NO temporal smoothing - zero ghost, instant response
    const pixelCount = segW * segH;
    // Skip smoothing entirely for maximum responsiveness

    const maskRGBA = new Uint8Array(pixelCount * 4);

    // Check if depth mask is available and matches dimensions
    const hasDepth = this.depthMaskData &&
                     this.depthMaskWidth === segW &&
                     this.depthMaskHeight === segH;

    if (hasDepth && this.frameCount % 300 === 0) {
      console.log(`[VideoEffects] Depth active, mode: ${this.useDepthOnly ? 'DEPTH-ONLY' : 'hybrid'}`);
    }

    for (let i = 0; i < pixelCount; i++) {
      let confidence;

      // MediaPipe confidence is the base
      confidence = maskData[i];

      // EDGE-ONLY DEPTH REFINEMENT: Only apply depth to uncertain/edge pixels
      // This avoids the "ghost" problem because we don't touch interior pixels
      if (hasDepth) {
        const depthValue = this.depthMaskData[i]; // 0=far, 1=close

        // Only refine pixels in the EDGE ZONE (uncertain confidence)
        // Interior pixels (very high or very low confidence) stay unchanged
        if (confidence > 0.25 && confidence < 0.75) {
          // This is an edge pixel - use depth to refine it
          // depthValue > 0.5 means closer (more likely person)
          // depthValue < 0.5 means further (more likely background)
          const depthBias = (depthValue - 0.5) * this.depthInfluence * 2;
          confidence = Math.max(0, Math.min(1, confidence + depthBias));
        }
      }

      const val = Math.round(confidence * 255);
      const dstIdx = i * 4;
      maskRGBA[dstIdx] = val;
      maskRGBA[dstIdx + 1] = val;
      maskRGBA[dstIdx + 2] = val;
      maskRGBA[dstIdx + 3] = 255;
    }

    // Cache mask for frame skipping
    this.lastMaskData = { maskRGBA, segW, segH };

    // Upload video
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.sourceVideo);

    // Upload mask directly at segment resolution (like backup version)
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, segW, segH, 0, gl.RGBA, gl.UNSIGNED_BYTE, maskRGBA);

    // Blur mask for antialiased edges (3 passes - good balance)
    for (let i = 0; i < 3; i++) {
      this.blurMask();
    }

    // Blur the background - optimized for performance
    const blurConfig = {
      light:  { amount: 2.5, passes: 3 },   // Stronger single-pass blur, fewer passes
      medium: { amount: 3.0, passes: 4 },
      heavy:  { amount: 3.5, passes: 5 },
    }[this.blurLevel] || { amount: 3.0, passes: 4 };

    for (let pass = 0; pass < blurConfig.passes; pass++) {
      this.blurBackground(blurConfig.amount);
    }

    // 4. Composite
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.programs.composite);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_original'), 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.bgBlurred);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_blurred'), 1);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.maskBlurred);  // Use blurred mask for smooth edges
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_mask'), 2);

    if (this.effectType === 'background' && this.backgroundImage) {
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, this.textures.background);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.backgroundImage);
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_background'), 3);
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_useBackground'), 1);
    } else {
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_useBackground'), 0);
    }

    gl.drawArrays(gl.TRIANGLES, 0, 6);
    result.close();
  }

  // PURE DEPTH-ONLY MODE: Bypass MediaPipe completely, use only depth mask
  renderWithDepthOnly() {
    const gl = this.gl;
    const w = this.sourceWidth;
    const h = this.sourceHeight;

    // Use depth mask dimensions
    const segW = this.depthMaskWidth;
    const segH = this.depthMaskHeight;

    if (!segW || !segH) {
      this.renderPassthrough();
      return;
    }

    // Create mask from depth data
    const pixelCount = segW * segH;
    const maskRGBA = new Uint8Array(pixelCount * 4);

    for (let i = 0; i < pixelCount; i++) {
      const depthValue = this.depthMaskData[i]; // 0=far, 1=close
      let confidence;

      // Convert depth to confidence with smooth transition
      if (depthValue < this.depthThresholdLow) {
        confidence = 0;
      } else if (depthValue > this.depthThresholdHigh) {
        confidence = 1;
      } else {
        const t = (depthValue - this.depthThresholdLow) / (this.depthThresholdHigh - this.depthThresholdLow);
        confidence = t * t * (3 - 2 * t); // smoothstep
      }

      const val = Math.round(confidence * 255);
      const idx = i * 4;
      maskRGBA[idx] = val;
      maskRGBA[idx + 1] = val;
      maskRGBA[idx + 2] = val;
      maskRGBA[idx + 3] = 255;
    }

    // Upload video
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.sourceVideo);

    // Upload depth mask directly at segment resolution
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, segW, segH, 0, gl.RGBA, gl.UNSIGNED_BYTE, maskRGBA);

    // Dilate to expand mask edges (3 passes)
    this.dilateMask();
    this.dilateMask();
    this.dilateMask();

    // Blur mask for smooth edges
    this.blurMask();

    // Blur background - optimized for performance
    const blurConfig = {
      light:  { amount: 2.5, passes: 3 },
      medium: { amount: 3.0, passes: 4 },
      heavy:  { amount: 3.5, passes: 5 },
    }[this.blurLevel] || { amount: 3.0, passes: 4 };

    for (let pass = 0; pass < blurConfig.passes; pass++) {
      this.blurBackground(blurConfig.amount);
    }

    // Composite
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.programs.composite);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_original'), 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.bgBlurred);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_blurred'), 1);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.maskBlurred);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_mask'), 2);

    if (this.effectType === 'background' && this.backgroundImage) {
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, this.textures.background);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.backgroundImage);
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_background'), 3);
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_useBackground'), 1);
    } else {
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_useBackground'), 0);
    }

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  // Render with cached mask (for frame skipping in low quality mode)
  renderWithCachedMask(timestamp) {
    if (!this.lastMaskData) {
      this.renderPassthrough();
      return;
    }

    const gl = this.gl;
    const w = this.sourceWidth;
    const h = this.sourceHeight;
    const { maskRGBA, segW, segH } = this.lastMaskData;

    // Upload current video frame
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.sourceVideo);

    // Upload cached mask directly at segment resolution
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, segW, segH, 0, gl.RGBA, gl.UNSIGNED_BYTE, maskRGBA);

    // Blur mask for antialiased edges (3 passes - good balance)
    for (let i = 0; i < 3; i++) {
      this.blurMask();
    }

    // Blur background - optimized for performance
    const blurConfig = {
      light:  { amount: 2.5, passes: 3 },
      medium: { amount: 3.0, passes: 4 },
      heavy:  { amount: 3.5, passes: 5 },
    }[this.blurLevel] || { amount: 3.0, passes: 4 };

    for (let pass = 0; pass < blurConfig.passes; pass++) {
      this.blurBackground(blurConfig.amount);
    }

    // Composite
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.programs.composite);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_original'), 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.bgBlurred);
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_blurred'), 1);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.maskBlurred);  // Use blurred mask
    gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_mask'), 2);

    if (this.effectType === 'background' && this.backgroundImage) {
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, this.textures.background);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.backgroundImage);
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_background'), 3);
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_useBackground'), 1);
    } else {
      gl.uniform1i(gl.getUniformLocation(this.programs.composite, 'u_useBackground'), 0);
    }

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  // Erosion: shrinks the mask to remove small false positives (chandelier, distant objects)
  erodeMask() {
    const gl = this.gl;
    // ALWAYS use source dimensions - framebuffers are at full resolution
    const w = this.sourceWidth;
    const h = this.sourceHeight;

    // Erode: mask -> maskTemp
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers.maskTemp);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.programs.maskErode);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.uniform1i(gl.getUniformLocation(this.programs.maskErode, 'u_mask'), 0);
    gl.uniform2f(gl.getUniformLocation(this.programs.maskErode, 'u_texelSize'), 1.0/w, 1.0/h);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Copy eroded result back to mask texture
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, w, h);
  }

  // Dilation: expands the mask to recover "eaten" edges (ears, hair tips, etc.)
  dilateMask() {
    const gl = this.gl;
    // ALWAYS use source dimensions - framebuffers are at full resolution
    const w = this.sourceWidth;
    const h = this.sourceHeight;

    // Dilate: mask -> maskTemp
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers.maskTemp);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.programs.maskDilate);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.uniform1i(gl.getUniformLocation(this.programs.maskDilate, 'u_mask'), 0);
    gl.uniform2f(gl.getUniformLocation(this.programs.maskDilate, 'u_texelSize'), 1.0/w, 1.0/h);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Copy dilated result back to mask texture
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, w, h);
  }

  blurMask() {
    const gl = this.gl;
    // ALWAYS use source dimensions - framebuffers are at full resolution
    const w = this.sourceWidth;
    const h = this.sourceHeight;

    // Horizontal pass: mask -> maskTemp
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers.maskTemp);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.programs.maskBlurH);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.uniform1i(gl.getUniformLocation(this.programs.maskBlurH, 'u_mask'), 0);
    gl.uniform2f(gl.getUniformLocation(this.programs.maskBlurH, 'u_texelSize'), 1.0/w, 1.0/h);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Vertical pass: maskTemp -> maskBlurred
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers.maskBlurred);
    gl.useProgram(this.programs.maskBlurV);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.maskTemp);
    gl.uniform1i(gl.getUniformLocation(this.programs.maskBlurV, 'u_mask'), 0);
    gl.uniform2f(gl.getUniformLocation(this.programs.maskBlurV, 'u_texelSize'), 1.0/w, 1.0/h);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Copy result back to mask for next iteration
    gl.bindTexture(gl.TEXTURE_2D, this.textures.mask);
    gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 0, 0, w, h, 0);
  }

  blurBackground(amount) {
    const gl = this.gl;
    const w = this.sourceWidth;
    const h = this.sourceHeight;

    // Horizontal pass: video -> bgTemp
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers.bgTemp);
    gl.viewport(0, 0, w, h);
    gl.useProgram(this.programs.bgBlurH);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.video);
    gl.uniform1i(gl.getUniformLocation(this.programs.bgBlurH, 'u_image'), 0);
    gl.uniform2f(gl.getUniformLocation(this.programs.bgBlurH, 'u_texelSize'), 1.0/w, 1.0/h);
    gl.uniform1f(gl.getUniformLocation(this.programs.bgBlurH, 'u_blurAmount'), amount);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Vertical pass: bgTemp -> bgBlurred
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffers.bgBlurred);
    gl.useProgram(this.programs.bgBlurV);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.bgTemp);
    gl.uniform1i(gl.getUniformLocation(this.programs.bgBlurV, 'u_image'), 0);
    gl.uniform2f(gl.getUniformLocation(this.programs.bgBlurV, 'u_texelSize'), 1.0/w, 1.0/h);
    gl.uniform1f(gl.getUniformLocation(this.programs.bgBlurV, 'u_blurAmount'), amount);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  setEffect(type) {
    if (['none', 'blur', 'background'].includes(type)) {
      this.effectType = type;
      console.log('[VideoEffects] Effect:', type);
    }
  }

  setBlurLevel(level) {
    if (['light', 'medium', 'heavy'].includes(level)) {
      this.blurLevel = level;
    }
  }

  async setBackgroundImage(url) {
    if (!url) { this.backgroundImage = null; return; }
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { this.backgroundImage = img; resolve(); };
      img.onerror = reject;
      img.src = url;
    });
  }

  getOutputTrack() {
    return this.outputStream?.getVideoTracks()[0];
  }

  stopProcessing() {
    this.isProcessing = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  cleanup() {
    this.stopProcessing();
    if (this.outputStream) {
      this.outputStream.getTracks().forEach(t => t.stop());
      this.outputStream = null;
    }
    if (this.segmenter) {
      this.segmenter.close();
      this.segmenter = null;
    }
    // Cleanup depth estimation
    this.depthService = null;
    this.depthMaskData = null;
    this.depthMaskWidth = 0;
    this.depthMaskHeight = 0;

    this.gl = null;
    this.isInitialized = false;
  }

  /**
   * Set depth influence level (how much depth affects uncertain pixels)
   * @param {number} influence - 0 to 1, default 0.25
   */
  setDepthInfluence(influence) {
    this.depthInfluence = Math.max(0, Math.min(1, influence));
    console.log(`[VideoEffects] Depth influence: ${this.depthInfluence}`);
  }

  /**
   * Force depth mask update
   */
  async refreshDepthMask() {
    if (!this.depthService) {
      console.log('[VideoEffects] Depth service not initialized');
      return;
    }
    await this.updateDepthMask();
  }

  /**
   * Get depth estimation status
   */
  getDepthStatus() {
    return {
      initialized: !!this.depthService,
      hasMask: !!this.depthMaskData,
      maskSize: this.depthMaskData ? `${this.depthMaskWidth}×${this.depthMaskHeight}` : 'N/A',
      influence: this.depthInfluence,
      depthOnlyMode: this.useDepthOnly,
      rescueThreshold: this.depthRescueThreshold,
      suppressThreshold: this.depthSuppressThreshold,
      thresholds: { low: this.depthThresholdLow, high: this.depthThresholdHigh },
    };
  }

  /**
   * Set suppress threshold - pixels with depth < this will be suppressed even if MediaPipe says person
   * @param {number} threshold - 0 to 1, default 0.35. Higher = more aggressive suppress (removes more background)
   */
  setDepthSuppressThreshold(threshold) {
    this.depthSuppressThreshold = Math.max(0, Math.min(1, threshold));
    console.log(`[VideoEffects] Depth suppress threshold: ${this.depthSuppressThreshold}`);
  }

  /**
   * Set rescue threshold - pixels with depth > this will be "rescued" even if MediaPipe says background
   * @param {number} threshold - 0 to 1, default 0.65. Lower = more aggressive rescue
   */
  setDepthRescueThreshold(threshold) {
    this.depthRescueThreshold = Math.max(0, Math.min(1, threshold));
    console.log(`[VideoEffects] Depth rescue threshold: ${this.depthRescueThreshold}`);
  }

  /**
   * Enable/disable depth-only mode (bypass MediaPipe)
   */
  setDepthOnlyMode(enabled) {
    this.useDepthOnly = enabled;
    console.log(`[VideoEffects] Depth-only mode: ${enabled ? 'ON' : 'OFF'}`);
  }

  /**
   * Set depth thresholds for depth-only mode
   * @param {number} low - Below this = background (0-1, default 0.35)
   * @param {number} high - Above this = foreground (0-1, default 0.55)
   */
  setDepthThresholds(low, high) {
    this.depthThresholdLow = Math.max(0, Math.min(1, low));
    this.depthThresholdHigh = Math.max(0, Math.min(1, high));
    console.log(`[VideoEffects] Depth thresholds: ${this.depthThresholdLow.toFixed(2)} - ${this.depthThresholdHigh.toFixed(2)}`);
  }
}

let processorInstance = null;

export function getVideoEffectsProcessor() {
  if (!processorInstance) processorInstance = new VideoEffectsProcessor();
  return processorInstance;
}

export function cleanupVideoEffectsProcessor() {
  if (processorInstance) {
    processorInstance.cleanup();
    processorInstance = null;
  }
}

// ============== GLOBAL DEBUG ACCESS ==============
// Access from browser console: window.videoEffectsDebug.getInfo()

if (typeof window !== 'undefined') {
  window.videoEffectsDebug = {
    // Get current debug info
    getInfo: () => {
      if (!processorInstance) return { error: 'No processor instance' };
      return processorInstance.getDebugInfo();
    },

    // Log debug info to console
    log: () => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      return processorInstance.logDebugInfo();
    },

    // Set quality level manually: 'high', 'medium', 'low'
    setQuality: (level) => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      processorInstance.setQualityLevel(level);
    },

    // Enable/disable auto quality adaptation
    setAutoAdapt: (enabled) => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      processorInstance.setAutoAdaptQuality(enabled);
    },

    // Get the processor instance directly
    getProcessor: () => processorInstance,

    // Available quality levels
    qualityLevels: QUALITY_LEVELS,

    // Start periodic debug logging (every N seconds)
    startPeriodicLog: (intervalSeconds = 5) => {
      if (window._videoEffectsDebugInterval) {
        clearInterval(window._videoEffectsDebugInterval);
      }
      window._videoEffectsDebugInterval = setInterval(() => {
        if (processorInstance) {
          const info = processorInstance.getDebugInfo();
          console.log(`[VideoEffects] FPS: ${info.fps} | Quality: ${info.qualityName} | ProcTime: ${info.avgProcessingTime}ms | Frames: ${info.frameCount}`);
        }
      }, intervalSeconds * 1000);
      console.log(`[VideoEffects] Periodic logging started (every ${intervalSeconds}s). Use stopPeriodicLog() to stop.`);
    },

    // Stop periodic logging
    stopPeriodicLog: () => {
      if (window._videoEffectsDebugInterval) {
        clearInterval(window._videoEffectsDebugInterval);
        window._videoEffectsDebugInterval = null;
        console.log('[VideoEffects] Periodic logging stopped.');
      }
    },

    // ============== DEPTH ESTIMATION DEBUG ==============

    // Run depth estimation and show debug window
    testDepth: async () => {
      // Dynamically import depth service
      const { getDepthEstimationService } = await import('./depthEstimation.js');
      const depthService = getDepthEstimationService();

      console.log('[Debug] Initializing depth estimation...');
      await depthService.initialize((progress) => {
        if (progress.status === 'downloading') {
          console.log(`[Debug] Downloading model: ${progress.percent}%`);
        }
      });

      // Get video element from processor
      if (!processorInstance?.sourceVideo) {
        console.log('[Debug] No video source available. Make sure video effects are active.');
        return;
      }

      console.log('[Debug] Running depth estimation...');
      await depthService.estimateDepth(processorInstance.sourceVideo);

      console.log('[Debug] Opening debug window...');
      depthService.openDebugWindow(processorInstance.sourceVideo);

      console.log('[Debug] Done! Check the popup window.');
      return depthService;
    },

    // Get depth service instance
    getDepthService: async () => {
      const { getDepthEstimationService } = await import('./depthEstimation.js');
      return getDepthEstimationService();
    },

    // Get depth status from processor
    depthStatus: () => {
      if (!processorInstance) return { error: 'No processor instance' };
      return processorInstance.getDepthStatus();
    },

    // Set depth influence (0-1, default 0.25)
    setDepthInfluence: (value) => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      processorInstance.setDepthInfluence(value);
    },

    // Force refresh depth mask
    refreshDepth: async () => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      await processorInstance.refreshDepthMask();
    },

    // Enable/disable depth-only mode
    setDepthOnly: (enabled) => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      processorInstance.setDepthOnlyMode(enabled);
    },

    // Set depth thresholds for depth-only mode
    setDepthThresholds: (low, high) => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      processorInstance.setDepthThresholds(low, high);
    },

    // Set rescue threshold (lower = more aggressive edge recovery)
    setRescueThreshold: (threshold) => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      processorInstance.setDepthRescueThreshold(threshold);
    },

    // Set suppress threshold (higher = more aggressive background removal)
    setSuppressThreshold: (threshold) => {
      if (!processorInstance) return console.log('[VideoEffects] No processor instance');
      processorInstance.setDepthSuppressThreshold(threshold);
    },
  };

  console.log('[VideoEffects] Debug tools available at window.videoEffectsDebug');
  console.log('[VideoEffects] Commands: setSuppressThreshold(0.35), setRescueThreshold(0.65), setDepthInfluence(0.4)');
}

export { VideoEffectsProcessor, QUALITY_LEVELS };
